/**
 * LetterEditor — the letter as it goes out, edited in place.
 *
 * Draws the same letterhead, type, colors and blocks the email and the Word
 * file carry (server letterRender.js), from the same text under the same
 * grammar (letterBlocks.js, a verbatim copy of the server's). Every block
 * is edited where it sits; a change is written back as the plain text the
 * renderers take, so what is saved, emailed and downloaded is what is on
 * the screen (operator order 2026-10-16: "must render as the letter itself,
 * and be editable, and be saved").
 *
 *   text        the letter, plain text
 *   onChange    (text) => void, after every committed edit
 *   theme       { orgName, brand, ink, muted, surface, fontHead, fontBody, footer }
 *   logoUrl     data URL of the organization logo, or null
 *   kicker      the small line under the organization name
 *   readOnly    show only
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { parseLetterBlocks, serializeLetterBlocks } from './letterBlocks';

const DEFAULT_THEME = { orgName: 'Compliance', brand: '1F4A3F', ink: '172B24', muted: '7C8A84', surface: 'F4F6F3', fontHead: 'Segoe UI', fontBody: 'Segoe UI', footer: '' };

/** One editable run of text: commits on blur, Enter commits unless multiline. */
const escHtml = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br/>');
function Editable({ value, onCommit, style, multiline = false, readOnly = false, autoFocus = false, placeholder = '' }) {
  const ref = useRef(null);
  useEffect(() => { if (autoFocus && ref.current) { ref.current.focus(); } }, [autoFocus]);
  // The text goes in as markup, not as React children: React rewrites the
  // element only when the value changes (after a commit), so while the user
  // types the browser owns the element and nothing it inserted can be
  // duplicated or dropped by a render.
  return (
    <div ref={ref} contentEditable={!readOnly} suppressContentEditableWarning spellCheck
      dangerouslySetInnerHTML={{ __html: escHtml(value) }}
      data-placeholder={placeholder}
      style={{ outline: 'none', minHeight: '1em', whiteSpace: multiline ? 'pre-wrap' : 'normal', cursor: readOnly ? 'default' : 'text', ...style }}
      className={readOnly ? '' : 'rounded-sm transition-shadow hover:shadow-[inset_0_0_0_1px_rgba(15,23,42,0.12)] focus:shadow-[inset_0_0_0_1px_rgba(15,23,42,0.35)] focus:bg-white'}
      onKeyDown={(e) => { if (e.key === 'Enter' && !multiline) { e.preventDefault(); e.currentTarget.blur(); } if (e.key === 'Escape') { e.currentTarget.innerText = value; e.currentTarget.blur(); } }}
      onBlur={(e) => { const t = e.currentTarget.innerText.replace(/ /g, ' ').replace(/\n{3,}/g, '\n\n').replace(/[ \t]+$/gm, '').trim(); if (t !== value) onCommit(t); }} />
  );
}

export default function LetterEditor({ text, onChange, theme: themeIn, logoUrl = null, kicker = '', readOnly = false }) {
  const T = { ...DEFAULT_THEME, ...(themeIn || {}) };
  const blocks = useMemo(() => parseLetterBlocks(text), [text]);
  const [focusKey, setFocusKey] = useState(null);
  const body = `${T.fontBody}, 'Segoe UI', Arial, Helvetica, sans-serif`;
  const head = `${T.fontHead}, 'Segoe UI', Arial, Helvetica, sans-serif`;
  const ink = `#${T.ink}`; const brand = `#${T.brand}`; const muted = `#${T.muted}`;
  const commit = (next) => onChange(serializeLetterBlocks(next));
  const update = (i, patch) => commit(blocks.map((b, k) => (k === i ? { ...b, ...patch } : b)));
  const remove = (i) => commit(blocks.filter((_, k) => k !== i));
  const insertAfter = (i, block) => { commit([...blocks.slice(0, i + 1), { type: 'blank' }, block, { type: 'blank' }, ...blocks.slice(i + 1)]); setFocusKey(`${i + 2}`); };
  // A paragraph cleared out is gone; a paragraph with a line break is two.
  const setParagraph = (i, t) => (t ? update(i, { text: t }) : remove(i));
  const Tools = ({ i, label }) => (readOnly ? null : (
    <span className="absolute -right-1 top-0 translate-x-full hidden group-hover:flex items-center gap-1 pl-2 text-[10px] font-semibold text-slate-400 whitespace-nowrap">
      <button type="button" title="Add a paragraph below" onClick={() => insertAfter(i, { type: 'paragraph', text: 'New paragraph' })} className="px-1.5 py-0.5 rounded bg-white border border-slate-200 hover:border-slate-400 hover:text-slate-700">+ ¶</button>
      <button type="button" title={`Remove this ${label}`} onClick={() => remove(i)} className="px-1.5 py-0.5 rounded bg-white border border-slate-200 hover:border-rose-300 hover:text-rose-700">×</button>
    </span>
  ));
  const metaGroups = [];
  blocks.forEach((b, i) => {
    const last = metaGroups[metaGroups.length - 1];
    if (b.type === 'meta' && last && last.type === 'metaGroup' && last.end === i - 1) { last.rows.push(i); last.end = i; return; }
    if (b.type === 'meta') { metaGroups.push({ type: 'metaGroup', rows: [i], end: i }); return; }
    metaGroups.push({ type: 'one', i });
  });
  const P = { margin: '0 0 12px 0', fontFamily: body, fontSize: 14, lineHeight: 1.6, color: ink };
  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden" style={{ background: '#f4f5f7' }}>
      <div style={{ padding: '24px 12px' }}>
        <div style={{ width: 680, maxWidth: '100%', margin: '0 auto', background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 10 }}>
          <div style={{ padding: '22px 32px 18px 32px', borderBottom: `3px solid ${brand}` }}>
            {logoUrl && <img src={logoUrl} alt={T.orgName} style={{ height: 40, maxWidth: 220, objectFit: 'contain', display: 'block', margin: '0 0 10px 0' }} />}
            <div style={{ fontFamily: head, fontSize: 16, fontWeight: 'bold', color: ink }}>{T.orgName}</div>
            {kicker && <div style={{ fontFamily: head, fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: muted, marginTop: 2 }}>{kicker}</div>}
          </div>
          <div style={{ padding: '24px 32px 28px 32px' }}>
            {metaGroups.map((grp, gi) => {
              if (grp.type === 'metaGroup') {
                return (
                  <table key={`m${gi}`} style={{ margin: '0 0 16px 0', borderCollapse: 'collapse' }}><tbody>
                    {grp.rows.map((i) => (
                      <tr key={i} className="group relative">
                        <td style={{ padding: '2px 14px 2px 0', fontFamily: head, fontSize: 12, fontWeight: 'bold', color: muted, whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                          <Editable value={blocks[i].key} readOnly={readOnly} onCommit={(t) => update(i, { key: t || blocks[i].key })} style={{ display: 'inline-block', minWidth: 40 }} />
                        </td>
                        <td style={{ padding: '2px 0', fontFamily: body, fontSize: 13, color: ink, verticalAlign: 'top' }}>
                          <Editable value={blocks[i].value} readOnly={readOnly} onCommit={(t) => update(i, { value: t || blocks[i].value })} />
                        </td>
                      </tr>
                    ))}
                  </tbody></table>
                );
              }
              const i = grp.i; const b = blocks[i]; const key = String(i);
              switch (b.type) {
                case 'title':
                  return (
                    <div key={key} className="group relative" style={{ margin: '0 0 10px 0' }}>
                      {b.kicker != null && <Editable value={b.kicker} readOnly={readOnly} onCommit={(t) => update(i, { kicker: t || null })} style={{ fontFamily: head, fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', color: brand, margin: '0 0 4px 0' }} />}
                      <Editable value={b.text} readOnly={readOnly} onCommit={(t) => update(i, { text: t || b.text })} style={{ fontFamily: head, fontSize: 18, fontWeight: 'bold', lineHeight: 1.35, color: ink }} />
                      <Tools i={i} label="title" />
                    </div>
                  );
                case 'heading':
                  return (
                    <div key={key} className="group relative" style={{ margin: '22px 0 10px 0', padding: '0 0 6px 0', borderBottom: `1px solid ${brand}`, fontFamily: head, fontSize: 12.5, fontWeight: 'bold', letterSpacing: '0.08em', textTransform: 'uppercase', color: brand, display: 'flex' }}>
                      <span style={{ display: 'inline-block', minWidth: 28 }}>{b.numeral}.</span>
                      <Editable value={b.text} readOnly={readOnly} onCommit={(t) => update(i, { text: t || b.text })} style={{ flex: 1 }} />
                      <Tools i={i} label="heading" />
                    </div>
                  );
                case 'subheading':
                  return (
                    <div key={key} className="group relative" style={{ margin: '14px 0 6px 0' }}>
                      <Editable value={b.text} readOnly={readOnly} onCommit={(t) => (t ? update(i, { text: t }) : remove(i))} style={{ fontFamily: head, fontSize: 14, fontWeight: 'bold', color: ink }} />
                      <Tools i={i} label="heading" />
                    </div>
                  );
                case 'bullets':
                  return (
                    <div key={key} className="group relative" style={{ margin: '0 0 12px 0' }}>
                      {b.items.map((it, k) => (
                        <div key={k} style={{ display: 'flex', alignItems: 'flex-start' }}>
                          <span style={{ padding: '2px 10px 2px 8px', fontFamily: body, fontSize: 14, lineHeight: 1.6, color: brand }}>•</span>
                          <Editable value={it} readOnly={readOnly} autoFocus={focusKey === `${i}:${k}`}
                            onCommit={(t) => { const items = b.items.slice(); if (t) items[k] = t; else items.splice(k, 1); items.length ? update(i, { items }) : remove(i); }}
                            style={{ padding: '2px 0', fontFamily: body, fontSize: 14, lineHeight: 1.6, color: ink, flex: 1 }} />
                        </div>
                      ))}
                      {!readOnly && (
                        <button type="button" onClick={() => { update(i, { items: [...b.items, 'New point'] }); setFocusKey(`${i}:${b.items.length}`); }}
                          className="ml-7 mt-0.5 text-[10.5px] font-semibold text-slate-400 hover:text-slate-700">+ point</button>
                      )}
                      <Tools i={i} label="list" />
                    </div>
                  );
                case 'detail':
                  return (
                    <div key={key} className="group relative" style={{ margin: '0 0 8px 0', display: 'flex' }}>
                      <span style={{ width: 14, borderLeft: `2px solid ${brand}22`, flex: 'none' }} />
                      <div style={{ padding: '0 0 0 12px', fontFamily: body, fontSize: 13, lineHeight: 1.55, color: ink, flex: 1, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {b.key && <Editable value={b.key} readOnly={readOnly} onCommit={(t) => update(i, { key: t || b.key })} style={{ fontWeight: 'bold', color: muted }} />}
                        {b.key && <span style={{ fontWeight: 'bold', color: muted }}>:</span>}
                        <Editable value={b.text} readOnly={readOnly} onCommit={(t) => (t ? update(i, { text: t }) : remove(i))} style={{ flex: 1 }} />
                      </div>
                      <Tools i={i} label="line" />
                    </div>
                  );
                case 'paragraph':
                  return (
                    <div key={key} className="group relative">
                      <Editable value={b.text} readOnly={readOnly} multiline autoFocus={focusKey === key} onCommit={(t) => setParagraph(i, t)} style={P} />
                      <Tools i={i} label="paragraph" />
                    </div>
                  );
                case 'table':
                  return (
                    <div key={key} className="group relative" style={{ margin: '6px 0 14px 0' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #d9dee3' }}>
                        <thead><tr>{b.header.map((h, c) => (
                          <th key={c} align="left" style={{ padding: '5px 7px', background: `#${T.surface}`, borderBottom: '1px solid #d9dee3', fontFamily: head, fontSize: 11, fontWeight: 'bold', color: ink, verticalAlign: 'bottom' }}>
                            <Editable value={h} readOnly={readOnly} onCommit={(t) => { const header = b.header.slice(); header[c] = t || h; update(i, { header }); }} />
                          </th>))}</tr></thead>
                        <tbody>{b.rows.map((r, ri) => (
                          <tr key={ri}>{r.map((cell, c) => (
                            <td key={c} style={{ padding: '4px 7px', borderBottom: '1px solid #edf0f3', fontFamily: body, fontSize: 11.5, lineHeight: 1.4, color: ink, verticalAlign: 'top' }}>
                              <Editable value={cell} readOnly={readOnly} onCommit={(t) => { const rows = b.rows.map((x) => x.slice()); rows[ri][c] = t; update(i, { rows }); }} />
                            </td>))}</tr>))}</tbody>
                      </table>
                      <Tools i={i} label="table" />
                    </div>
                  );
                case 'signature':
                  return (
                    <div key={key} className="group relative" style={{ margin: '24px 0 0 0', paddingTop: 12, borderTop: '1px solid #e2e8f0' }}>
                      {b.lines.map((l, k) => (
                        <Editable key={k} value={l} readOnly={readOnly} onCommit={(t) => { const lines = b.lines.slice(); if (t) lines[k] = t; else lines.splice(k, 1); lines.length ? update(i, { lines }) : remove(i); }}
                          style={{ fontFamily: head, fontSize: 13, lineHeight: 1.5, color: ink, fontWeight: k === 0 ? 'bold' : 'normal' }} />
                      ))}
                    </div>
                  );
                case 'blank':
                  return null;
                default:
                  return null;
              }
            })}
            {!readOnly && (
              <button type="button" onClick={() => { const sigAt = blocks.findIndex((b) => b.type === 'signature'); const at = sigAt >= 0 ? sigAt - 1 : blocks.length - 1; insertAfter(at, { type: 'paragraph', text: 'New paragraph' }); }}
                className="mt-2 text-[10.5px] font-semibold text-slate-400 hover:text-slate-700">+ Add a paragraph</button>
            )}
          </div>
          <div style={{ padding: '12px 32px', background: `#${T.surface}`, borderRadius: '0 0 10px 10px', fontFamily: body, fontSize: 11, color: muted }}>{T.footer || `${T.orgName} Compliance · Audit and Monitoring`}</div>
        </div>
      </div>
    </div>
  );
}
